import { Text, VStack } from '@chakra-ui/react';
import { useEffect } from 'react';

import Modal from '../../../../../../components/Modal';
import Button from '../../../../../../components/ui/buttons/Button';
import { trackBuyoutOverlayOpened } from '../../../../../../services/analytics/marketplaceEvents';
import { GateVariant } from '../../guidance';

type ConfirmBuyoutModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  variant: GateVariant;
  isPending: boolean;
  errorMessage: string | null;
  dealId: string;
  leadId: string;
  model?: string | null;
  payoffAmount: string | null;
  taxLine?: string;
  premiumAboveValue: string | null;
};

const ConfirmBuyoutModal = ({
  isOpen,
  onClose,
  onConfirm,
  variant,
  isPending,
  errorMessage,
  dealId,
  leadId,
  model,
  payoffAmount,
  taxLine,
  premiumAboveValue,
}: ConfirmBuyoutModalProps) => {
  useEffect(() => {
    if (isOpen) trackBuyoutOverlayOpened(dealId, leadId, `gate_${variant}`);
  }, [isOpen, dealId, leadId, variant]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={!isPending}
      title="Keep your car."
      titleFontSize={{ base: '22px', md: '26px' }}
      titleFontWeight="700"
      _content={{ maxW: '480px' }}
      _body={{ pb: '28px' }}
    >
      <VStack align="stretch" spacing="14px">
        {payoffAmount != null && (
          <>
            <Text fontSize="18px" fontWeight="700" color="leaseEndBlue" lineHeight="1.3">
              Own your {model || 'car'} for about {payoffAmount}
            </Text>
            {taxLine != null && (
              <Text fontSize="11.5px" color="grayLight" lineHeight="1.5" mt="-10px">
                {taxLine}
              </Text>
            )}
          </>
        )}
        <Text fontSize="13.5px" color="charcoal" lineHeight="1.6">
          A lease buyout pays off the lease and puts the title in your name. No selling, no check to
          write today, and you keep the car you know.
          {premiumAboveValue != null && (
            <>
              {' '}
              That's about {premiumAboveValue} more than the car's current value, so it only makes
              sense if you plan to drive it for years to come.
            </>
          )}
        </Text>
        <Text fontSize="13.5px" color="charcoal" lineHeight="1.6">
          Choosing this puts your listing on hold. Our buyout team walks you through your exact
          total, financing, and next steps, and it starts with one call.
        </Text>
        {errorMessage != null && (
          <Text fontSize="12.5px" color="leaseEndRed">
            {errorMessage}
          </Text>
        )}
        <Button onClick={onConfirm} isLoading={isPending} size="sm" w="100%" h="40px">
          Yes, start my buyout
        </Button>
        <Button
          onClick={onClose}
          isDisabled={isPending}
          variant="blueOutline"
          size="sm"
          w="100%"
          h="40px"
        >
          Not yet
        </Button>
      </VStack>
    </Modal>
  );
};

export default ConfirmBuyoutModal;
